/**
 * PATH: apps/quote/src/components/builder/panels/TakeoffAssistPanel.tsx
 * PURPOSE: AI-powered takeoff assistance panel orchestrator
 * WHY: Fast product/application lookup, AI suggestions, document extraction during takeoff
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Search, Package, Layers, Sparkles, Target, Loader2, FileSearch, Lightbulb, RefreshCw, X, FileText } from 'lucide-react'
import { cn } from '@/lib/utils'
import { quoteApi, type DocumentExtractedItem, type TakeoffCoverageGap } from '@/lib/quoteApi'
import { useToast } from '../Toast'

// Sub-modules
import { QUICK_FILTERS, type AssistTab, type TakeoffAssistPanelProps } from './takeoff-assist/takeoff-assist-types'
import SearchResultItem from './takeoff-assist/SearchResultItem'
import SuggestedAppCard from './takeoff-assist/SuggestedAppCard'
import SimilarRowCard from './takeoff-assist/SimilarRowCard'
import CoverageGapCard from './takeoff-assist/CoverageGapCard'
import DocumentItemCard from './takeoff-assist/DocumentItemCard'
import RowNavigator from './takeoff-assist/RowNavigator'

export default function TakeoffAssistPanel({
  quoteId, sections, selectedSectionId, selectedItemId,
  onApplyApplication, onJumpToRow, onAddRow, onAddRowWithDetails,
}: TakeoffAssistPanelProps) {
  const [activeTab, setActiveTab] = useState<AssistTab>('search')
  const [searchQuery, setSearchQuery] = useState('')
  const [activeFilter, setActiveFilter] = useState<string | null>(null)
  const [appliedDocItems, setAppliedDocItems] = useState<Set<string>>(new Set())
  const searchInputRef = useRef<HTMLInputElement>(null)
  const toast = useToast()

  useEffect(() => { searchInputRef.current?.focus() }, [])

  const effectiveQuery = useMemo(() => {
    const filter = QUICK_FILTERS.find(f => f.id === activeFilter)
    if (filter && searchQuery) return `${filter.query} ${searchQuery}`
    return filter?.query || searchQuery
  }, [searchQuery, activeFilter])

  const selectedItem = useMemo(() => {
    if (!selectedSectionId || !selectedItemId) return null
    const section = sections.find(s => s.id === selectedSectionId)
    return section?.items?.find(i => i.id === selectedItemId) || null
  }, [sections, selectedSectionId, selectedItemId])

  const { data: applications = [], isLoading: isSearching } = useQuery({
    queryKey: ['applications', 'search', effectiveQuery],
    queryFn: () => quoteApi.searchApplications(effectiveQuery),
    enabled: effectiveQuery.length >= 2, staleTime: 30 * 1000,
  })

  const { data: aiSuggestions, isLoading: isSuggestionsLoading, refetch: refetchSuggestions } = useQuery({
    queryKey: ['takeoffSuggestions', quoteId, selectedItemId, selectedItem?.description],
    queryFn: () => quoteApi.getTakeoffSuggestions(quoteId, { line_item_id: selectedItemId || undefined, section_id: selectedSectionId || undefined, description: selectedItem?.description || '', location: selectedItem?.location || '' }),
    enabled: !!quoteId && (!!selectedItemId || !!selectedItem), staleTime: 60 * 1000,
  })

  const { data: documentTakeoff, isLoading: isDocLoading, refetch: refetchDocuments } = useQuery({
    queryKey: ['documentTakeoff', quoteId],
    queryFn: () => quoteApi.getDocumentTakeoffItems(quoteId),
    enabled: !!quoteId && activeTab === 'documents', staleTime: 5 * 60 * 1000,
  })

  const applyMutation = useMutation({
    mutationFn: ({ itemId, applicationId }: { itemId: string; applicationId: string }) => quoteApi.applyTakeoffApplication(quoteId, itemId, applicationId),
    onSuccess: () => toast.success('Application applied'),
    onError: () => toast.error('Failed to apply application'),
  })

  const handleApplyToRow = useCallback((appId: string) => { if (selectedSectionId && selectedItemId) onApplyApplication(selectedSectionId, selectedItemId, appId) }, [selectedSectionId, selectedItemId, onApplyApplication])
  const handleAddAsNewRow = useCallback((appId: string) => { if (selectedSectionId) onAddRow(selectedSectionId, appId) }, [selectedSectionId, onAddRow])
  const handleApplyAISuggestion = useCallback((appId: string) => { if (selectedItemId) applyMutation.mutate({ itemId: selectedItemId, applicationId: appId }) }, [selectedItemId, applyMutation])

  const handleApplyDocItem = useCallback((item: DocumentExtractedItem) => {
    if (!selectedSectionId || !onAddRowWithDetails) { toast.info('Select a section to add items with full details'); return }
    onAddRowWithDetails(selectedSectionId, { description: item.description, quantity: item.quantity, unit: item.unit, location: item.location, source_document_id: item.source_document_id })
    setAppliedDocItems(prev => new Set([...prev, item.id]))
    toast.success('Item added to quote')
  }, [selectedSectionId, onAddRowWithDetails, toast])

  const handleAddCoverageGap = useCallback((gap: TakeoffCoverageGap) => {
    if (!selectedSectionId || !onAddRowWithDetails) { toast.info('Select a section to add coverage gap item'); return }
    onAddRowWithDetails(selectedSectionId, { description: gap.description, quantity: 1, unit: 'm²' })
    toast.success('Coverage gap item added')
  }, [selectedSectionId, onAddRowWithDetails, toast])

  const tabItems = [
    { id: 'search' as const, label: 'Search', icon: Search },
    { id: 'suggestions' as const, label: 'AI', icon: Sparkles },
    { id: 'documents' as const, label: 'Docs', icon: FileSearch },
    { id: 'history' as const, label: 'Navigate', icon: Layers },
  ]

  return (
    <div className="flex flex-col h-full">
      {/* Search header */}
      <div className="p-3 border-b border-slate-200 dark:border-slate-800">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input ref={searchInputRef} type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search applications, products..." className="w-full pl-8 pr-3 py-2 text-sm bg-slate-100 dark:bg-slate-800 border-0 rounded-lg focus:ring-2 focus:ring-blue-500" />
          {searchQuery && (<button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded"><X className="w-3.5 h-3.5 text-slate-400" /></button>)}
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {QUICK_FILTERS.map(filter => { const Icon = filter.icon; const isActive = activeFilter === filter.id; return (
            <button key={filter.id} onClick={() => setActiveFilter(isActive ? null : filter.id)}
              className={cn('flex items-center gap-1 px-2 py-1 text-xs rounded-full transition-colors', isActive ? 'bg-blue-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700')}>
              <Icon className="w-3 h-3" />{filter.label}
            </button>
          )})}
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-slate-200 dark:border-slate-800">
        {tabItems.map(tab => { const Icon = tab.icon; return (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={cn('flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors', activeTab === tab.id ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 hover:text-slate-700')}>
            <Icon className="w-3.5 h-3.5" />{tab.label}
          </button>
        )})}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3">
        {activeTab === 'search' && (
          <div className="space-y-2">
            {isSearching ? (<div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>)
            : effectiveQuery.length < 2 ? (<div className="text-center py-8"><Search className="w-8 h-8 mx-auto text-slate-300 dark:text-slate-600 mb-2" /><p className="text-sm text-slate-500">Type to search applications</p><p className="text-xs text-slate-400 mt-1">Or use quick filters above</p></div>)
            : applications.length === 0 ? (<div className="text-center py-8"><Package className="w-8 h-8 mx-auto text-slate-300 dark:text-slate-600 mb-2" /><p className="text-sm text-slate-500">No applications found</p></div>)
            : applications.map(app => (<SearchResultItem key={app.id} application={app} onSelect={() => handleApplyToRow(app.id)} onAddAsNew={() => handleAddAsNewRow(app.id)} isSelected={false} />))}
          </div>
        )}

        {activeTab === 'suggestions' && (
          <div className="space-y-4">
            {!selectedItem ? (<div className="text-center py-8"><Target className="w-8 h-8 mx-auto text-slate-300 dark:text-slate-600 mb-2" /><p className="text-sm text-slate-500">Select a row for AI suggestions</p></div>)
            : isSuggestionsLoading ? (<div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-purple-500" /><span className="ml-2 text-sm text-slate-500">Analyzing...</span></div>)
            : (<>
              <div className="text-xs text-slate-500 pb-2 border-b border-slate-200 dark:border-slate-800"><span className="font-medium">Context:</span> {selectedItem.description || selectedItem.location || 'Selected row'}</div>
              {aiSuggestions?.suggested_applications?.length > 0 && (<div><div className="flex items-center gap-1.5 mb-2"><Lightbulb className="w-4 h-4 text-purple-500" /><h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300">Suggested Applications</h4></div><div className="space-y-2">{aiSuggestions.suggested_applications.map(app => (<SuggestedAppCard key={app.id} app={app} onApply={() => handleApplyAISuggestion(app.id)} onAddNew={() => handleAddAsNewRow(app.id)} isApplying={applyMutation.isPending} />))}</div></div>)}
              {aiSuggestions?.similar_rows?.length > 0 && (<div><h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">Similar Rows</h4><div className="space-y-1.5">{aiSuggestions.similar_rows.map(row => (<SimilarRowCard key={row.id} row={row} onJump={() => onJumpToRow(row.section_id, row.id)} onCopyApplication={() => { if (row.application_id && selectedItemId && selectedSectionId) onApplyApplication(selectedSectionId, selectedItemId, row.application_id) }} />))}</div></div>)}
              {aiSuggestions?.coverage_gaps?.length > 0 && (<div><h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">Coverage Gaps</h4><div className="space-y-2">{aiSuggestions.coverage_gaps.map((gap, idx) => (<CoverageGapCard key={idx} gap={gap} onAdd={() => handleAddCoverageGap(gap)} />))}</div></div>)}
              {(!aiSuggestions?.suggested_applications?.length && !aiSuggestions?.similar_rows?.length && !aiSuggestions?.coverage_gaps?.length) && (<div className="text-center py-6"><Sparkles className="w-8 h-8 mx-auto text-slate-300 dark:text-slate-600 mb-2" /><p className="text-sm text-slate-500">No suggestions available</p><button onClick={() => refetchSuggestions()} className="mt-2 text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 mx-auto"><RefreshCw className="w-3 h-3" />Refresh</button></div>)}
            </>)}
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300">Extracted from Documents</h4>
              <button onClick={() => refetchDocuments()} disabled={isDocLoading} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" title="Refresh extraction">
                <RefreshCw className={cn('w-3.5 h-3.5 text-slate-400', isDocLoading && 'animate-spin')} />
              </button>
            </div>
            {isDocLoading ? (<div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-blue-500" /><span className="ml-2 text-sm text-slate-500">Extracting items...</span></div>)
            : documentTakeoff?.extraction_status === 'no_documents' || !documentTakeoff?.items?.length ? (<div className="text-center py-8"><FileSearch className="w-8 h-8 mx-auto text-slate-300 dark:text-slate-600 mb-2" /><p className="text-sm text-slate-500">No documents to extract from</p></div>)
            : (<>
              {documentTakeoff.source_documents.length > 0 && (<div className="flex flex-wrap gap-1.5 mb-3">{documentTakeoff.source_documents.map(doc => (<span key={doc.id} className={cn('text-[10px] px-2 py-1 rounded-full flex items-center gap-1', doc.extraction_status === 'complete' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : doc.extraction_status === 'processing' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400')}><FileText className="w-3 h-3" />{doc.name.length > 15 ? `${doc.name.slice(0, 15)}...` : doc.name}</span>))}</div>)}
              <div className="space-y-2">{documentTakeoff.items.map(item => (<DocumentItemCard key={item.id} item={item} onApply={() => handleApplyDocItem(item)} isApplied={appliedDocItems.has(item.id)} isApplying={false} />))}</div>
            </>)}
          </div>
        )}

        {activeTab === 'history' && (<div><p className="text-xs text-slate-500 mb-2">Jump to row</p><RowNavigator sections={sections} selectedSectionId={selectedSectionId} selectedItemId={selectedItemId} onJumpToRow={onJumpToRow} /></div>)}
      </div>

      {selectedItem && (
        <div className="p-2 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950">
          <div className="flex items-center gap-2 text-xs">
            <Target className="w-3.5 h-3.5 text-blue-500" /><span className="text-slate-500">Target:</span>
            <span className="text-slate-700 dark:text-slate-300 font-medium truncate">{selectedItem.description || selectedItem.location || 'Selected row'}</span>
          </div>
        </div>
      )}
    </div>
  )
}
